import React from 'react'
import moment from 'moment'
import _ from 'underscore'
import { Check, Ghost, GraduationCap, Laptop, Mars, Phone, Star, TriangleAlert, Trophy, User, Venus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import TutorResult from './show'
import Timeago from '../../../../timeago'
import store from '../../../../../state/store'

const STATE_BADGE_CLASSES = 'flex h-auto w-full whitespace-normal'
const WARNING_BADGE_CLASSES = 'my-px h-auto max-w-full whitespace-normal'

class TutorResultsPartial extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      viewing: false,
      noteExpanded: false
    }
  }

  toggleNote = (e) => {
    e.stopPropagation()
    this.setState({ noteExpanded: !this.state.noteExpanded })
  }

  onSelectChange = () => {
    this.props.onToggleSelect(this.props.tutor_result.tutor.id)
  }

  onToggleView = () => {
    this.setState({ viewing: !this.state.viewing })

    if (this.state.viewing) {
      this.setState({viewing: false})
    }
    else {
      store.emit('tutoring_job:tutor_results:show', this.props.tutoring_job.id, this.props.tutor_result.tutor.id)
      this.setState({viewing: true})
    }
  }

  renderGender = () => {
    const tutor_gender = this.props.tutor_result.tutor.gender

    if (tutor_gender === 'female')
      return (
        <Venus className="size-3.5" title="Female"/>
      )
    else if (tutor_gender === 'male')
      return (
        <Mars className="size-3.5" title="Male"/>
      )

    return null
  }

  renderState = () => {
    const job_candidate = this.props.tutor_result.job_candidate

    if (job_candidate.rejected)
      return <Badge variant="destructive" className={STATE_BADGE_CLASSES}>Rejected</Badge>
    else if (job_candidate.accepted)
      return <Badge variant="success" className={STATE_BADGE_CLASSES}>Accepted</Badge>
    else if (job_candidate.declined)
      return <Badge variant="destructive" className={STATE_BADGE_CLASSES}>Declined</Badge>
    else if (job_candidate.proposed)
      return <Badge variant="success" className={STATE_BADGE_CLASSES}>Proposed</Badge>
    else if (job_candidate.applied)
      return (
        <Badge className={`${STATE_BADGE_CLASSES} border-transparent bg-[#1e854c] text-white`}>
          <span className="font-semibold">Applied</span>
        </Badge>
      )
    else if (job_candidate.interested)
      return <Badge className={STATE_BADGE_CLASSES}>Incomplete</Badge>
    else if (job_candidate.notified)
      return (
        <Badge className={STATE_BADGE_CLASSES}>
          Notified <Timeago date={job_candidate.created_at} />
        </Badge>
      )
    else
      return <Badge variant="secondary" className={STATE_BADGE_CLASSES}>Can Notify</Badge>
  }

  renderCallState = () => {
    const call_outcome = this.props.tutor_result.job_candidate.call_outcome

    if (call_outcome === 'no_answer')
      return (
        <Badge variant="info" className={STATE_BADGE_CLASSES}><Phone className="size-3"/>No Answer</Badge>
      )
    else if (call_outcome === 'keen')
      return (
        <Badge variant="success" className={STATE_BADGE_CLASSES}><Phone className="size-3"/>Tutor Keen</Badge>
      )
    else if (call_outcome === 'not_keen')
      return (
        <Badge variant="destructive" className={STATE_BADGE_CLASSES}><Phone className="size-3"/><span className="font-bold">NOT</span> Keen</Badge>
      )

    return null
  }

  renderAvailableFrom = () => {
    const available_from = this.props.tutor_result.job_candidate.available_from

    if (available_from === "today")
      return <span className="text-sm">Can Start: Today</span>
    else if (available_from === "this_week")
      return <span className="text-sm">Can Start: This week</span>
    else if (available_from === "next_week")
      return <span className="text-sm">Can Start: Next week</span>

    return null
  }

  renderDistance = () => {
    if (this.props.tutor_result.route_distance)
      return <span><span className="font-bold">{this.props.tutor_result.route_distance}</span> km</span>
    else if (this.props.tutor_result.distance)
      return <span><span className="font-bold">~ {this.props.tutor_result.distance}</span> km</span>

    return null
  }

  renderActivityWarning = () => {
    const activity_at = this.props.tutor_result.tutor.activity_at

    if (!activity_at) {
      return null
    }

    if (moment().diff(moment(activity_at), 'days') < 30) {
      return null
    }

    return (
      <Badge variant="warning" className={WARNING_BADGE_CLASSES}>
        <TriangleAlert className="size-3"/> Last Active <Timeago date={activity_at} />
      </Badge>
    )
  }

  renderTransportWarning = () => {
    if (this.props.tutoring_job.medium == 'online') {
      return null
    }

    if (this.props.tutor_result.tutor.has_transport) {
      return null
    }

    return (
      <Badge variant="warning" className={WARNING_BADGE_CLASSES}>
        <TriangleAlert className="size-3"/> No Transport
      </Badge>
    )
  }

  renderCommitmentWarning = () => {
    const customer_commitment = this.props.customer_commitment
    const tutor_commitment = this.props.tutor_result.job_candidate.commitment

    const warning = (
      <Badge variant="warning" className={WARNING_BADGE_CLASSES}>
        <TriangleAlert className="size-3"/> Commitments Differ
      </Badge>
    )

    if (customer_commitment === "0_to_2_weeks" && (tutor_commitment !== "0_to_2_weeks"))
      return warning
    else if ((customer_commitment === "2_to_4_weeks") && (tutor_commitment === "0_to_2_weeks"))
      return warning
    else if ((customer_commitment === "1_to_3_months") && (["2_to_4_weeks", "0_to_2_weeks"].includes(tutor_commitment)))
      return warning
    else if ((customer_commitment === "3_to_6_months") && (["1_to_3_months", "2_to_4_weeks", "0_to_2_weeks"].includes(tutor_commitment)))
      return warning
    else if ((customer_commitment === "six_plus_months") && (["3_to_6_months", "1_to_3_months", "2_to_4_weeks", "0_to_2_weeks"].includes(tutor_commitment)))
      return warning

    return null
  }

  renderTutorWarning = () => {
    if (this.props.tutor_result.tutor_warnings.length > 0) {
      return (
        <Badge variant="warning" className={cn(WARNING_BADGE_CLASSES, 'cursor-pointer')}>
          <TriangleAlert className="size-3"/>Warnings Issued
        </Badge>
      )
    }

    return null
  }

  // Read-only; false alarms are left out by the API.
  renderCircumvention = () => {
    const incidents = this.props.tutor_result.circumvention_incidents || []

    if (incidents.length === 0) {
      return null
    }

    const verified = incidents.some((incident) => incident.outcome === 'verified')

    return (
      <Badge variant={verified ? 'destructive' : 'warning'} className={WARNING_BADGE_CLASSES}>
        <TriangleAlert className="size-3"/>{verified ? 'Circumvention Verified' : 'Circumvention Pending'}
      </Badge>
    )
  }

  renderGhosted = () => {
    if (this.props.tutor_result.tutor.ghosted) {
      return (
        <Badge variant="destructive" className={cn(WARNING_BADGE_CLASSES, 'cursor-pointer')}>
          <Ghost className="size-3"/>Ghosted
        </Badge>
      )
    }

    return null
  }

  renderPositionState = (tutoring_job_position) => {
    const proposed = this.props.tutor_result.proposed_tutoring_job_position_ids.includes(tutoring_job_position.id)
    const accepted = this.props.tutor_result.accepted_tutoring_job_position_ids.includes(tutoring_job_position.id)

    if (accepted)
      return (
        <Badge variant="success" className="flex w-full font-normal">Accepted</Badge>
      )
    else if (proposed) {
      const auto_proposed = _.any(this.props.tutor_proposals, function (tutor_proposal) {
        return ((tutor_proposal.tutoring_job_position_id === tutoring_job_position.id) && (tutor_proposal.auto === true))
      })

      if (auto_proposed)
        return (
          <Badge variant="success" className="flex w-full font-normal">Auto-Proposed</Badge>
        )
      else
        return (
          <Badge variant="success" className="flex w-full font-normal">Proposed</Badge>
        )
    }

    return null
  }

  renderSubjectInfo = (tutoring_job_position) => {
    const tutor_subject = _.first(
      _.select(this.props.tutor_result.matching_tutor_subjects, function (tutor_subject) {
        return tutor_subject.subject_name === tutoring_job_position.subject_name
      })
    )

    if (tutor_subject) {
      return (
        <div className="w-3/4 px-1">
          <Badge variant="success" className="flex h-auto w-full justify-start whitespace-normal rounded-lg px-2.5 py-1 text-left font-normal capitalize leading-snug">
            <span>
              <span className="font-semibold">{tutoring_job_position.subject_name}</span>{' '}
              <span className="text-xs opacity-90">
                {tutor_subject.subject_level_name}
                {tutor_subject.syllabus ?
                  <span> ({tutor_subject.syllabus})</span>
                  : null
                }
              </span>
            </span>
          </Badge>
        </div>
      )
    }

    const learner_subject = _.first(_.select(this.props.learner_subjects, function (learner_subject) {
      return learner_subject.name === tutoring_job_position.subject_name
    }))

    return (
      <div className="w-3/4 px-1">
        <Badge variant="secondary" className="flex h-auto w-full justify-start whitespace-normal rounded-lg px-2.5 py-1 text-left font-normal capitalize leading-snug">
          <span>
            <span className="font-semibold">{tutoring_job_position.subject_name}</span>{' '}
            <span className="text-xs opacity-90">
              {tutoring_job_position.subject_level_name}
              {((learner_subject || {}).syllabus && (learner_subject.syllabus !== "None / I don't know")) ?
                <span> ({learner_subject.syllabus})</span>
                : null
              }
            </span>
          </span>
        </Badge>
      </div>
    )
  }

  renderCommitmentPeriod = () => {
    const commitment_period = this.props.tutor_result.job_candidate.commitment_period

    if (commitment_period) {
      return (
        <span className="text-sm">Commit Till: <Timeago date={commitment_period}/></span>
      )
    }

    return null
  }

  render() {
    const tutor_result = this.props.tutor_result
    const tutor = tutor_result.tutor

    const has_matching_availability = tutor_result.has_matching_availability
    const profile_matches_availability = tutor_result.profile_matches_availability

    const application_note = tutor_result.job_candidate.application_note || ''
    const NOTE_LIMIT = 180
    const note_is_long = application_note.length > NOTE_LIMIT
    const note_display = note_is_long && !this.state.noteExpanded
      ? application_note.slice(0, NOTE_LIMIT).trimEnd() + '…'
      : application_note

    return (
      <div>
        <div
          className={cn(
            '@container min-h-[56px] w-full cursor-pointer rounded-[10px] border border-border bg-card px-4 py-3 text-center text-muted-foreground transition-colors',
            this.state.viewing ?
              'rounded-b-none border-primary/40 bg-primary/5'
            :
              'hover:bg-muted/50'
          )}
          onClick={this.onToggleView}
        >
          <div className="flex items-center">
            <div
              className="flex w-8 shrink-0 items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {this.props.selectable && (
                <Checkbox
                  checked={this.props.selected}
                  onCheckedChange={this.onSelectChange}
                  aria-label={`Select ${tutor.name}`}
                />
              )}
            </div>
            <div className="w-[23%] text-left text-xs capitalize">
              {/* Narrow rows (e.g. beside a docked panel) stack the photo so the details get the column's width */}
              <div className="flex items-center gap-3 @max-4xl:flex-col @max-4xl:items-stretch @max-4xl:gap-2">
                <div className="shrink-0 pl-2 @max-4xl:pl-0">
                  <img className="block size-16 rounded-full" src={tutor.profile_picture_url} alt="Mug shot" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold text-foreground">
                    {tutor.name}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-sm text-muted-foreground [&_svg]:text-primary">
                    <span className="inline-flex items-center gap-1">
                      {this.renderGender()}
                      {tutor.age}
                    </span>
                    <span className="inline-flex items-center gap-1"><GraduationCap className="size-3.5" title={`${tutor.hours_tutored} hours tutored`}/>{tutor.hours_tutored}</span>
                    {tutor.rating ?
                      <span className="inline-flex items-center gap-1"><Star className="size-3.5" title={`${tutor.rating_count} customer reviews`}/>{tutor.rating}</span>
                      : null
                    }
                    {tutor_result.tutor.top_tier ?
                      <span className="inline-flex items-center gap-1"><Trophy className="size-3.5" title="Top Tier Tutor"/></span>
                      : null
                    }
                  </div>
                  <div className="mt-1 flex w-full max-w-[135px] flex-col items-start gap-1 text-[13px]">
                    {tutor_result.job_candidate.shortlisted ?
                      <Badge className={STATE_BADGE_CLASSES}>Shortlisted</Badge>
                      : null
                    }
                    {this.renderState()}
                    {this.renderCallState()}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-[35%] space-y-1.5">
              {_.map(this.props.tutoring_job.tutoring_job_positions, (tutoring_job_position) => {
                return (
                  <div className="flex items-center" key={tutoring_job_position.id}>
                    <div className="w-1/4">
                      {this.renderPositionState(tutoring_job_position)}
                    </div>
                    {this.renderSubjectInfo(tutoring_job_position)}
                  </div>
                )
              })}
            </div>
            <div className="w-[17%]">
              <div>
                {this.renderAvailableFrom()}
              </div>
              <div>
                {this.renderCommitmentPeriod()}
              </div>
              {has_matching_availability ?
                <span>Matched Availability: <Check className="inline size-3.5 text-[#3ecf8e]"/></span>
                : profile_matches_availability ?
                  <span className="text-xs italic text-muted-foreground" title="Tutor's profile availability overlaps the client's preferred times. Job-specific availability not yet set.">
                    <User className="mr-1 inline size-3.5"/>Profile suggests match
                  </span>
                  : null
              }
            </div>
            <div className="w-[8%]">
              <div>
                {this.renderDistance()}
              </div>
              {tutor.online_classroom_tutoring ?
                <span title="Offers Online Classroom Tutoring"><Laptop className="inline size-3.5"/></span>
                : null
              }
            </div>
            <div className="flex w-[17%] flex-col items-center">
              {this.renderGhosted()}
              {this.renderActivityWarning()}
              {this.renderTransportWarning()}
              {this.renderCommitmentWarning()}
              {this.renderTutorWarning()}
              {this.renderCircumvention()}
            </div>
          </div>
          {application_note.length > 0 ?
            <div className="mx-auto w-5/6">
              <p className="text-center text-muted-foreground">
                "{note_display}"
                {note_is_long ?
                  <button
                    type="button"
                    onClick={this.toggleNote}
                    className="ml-1 font-medium text-primary hover:underline"
                  >
                    {this.state.noteExpanded ? 'Show less' : 'Read more'}
                  </button>
                  : null
                }
              </p>
            </div>
            : null
          }
        </div>
        {this.state.viewing ?
          <TutorResult
            tutor_result={tutor_result}
            tutoring_job={this.props.tutoring_job}
            tutor_proposals={this.props.tutor_proposals}
            ui={this.props.ui}
            selected={this.state.viewing}
            onToggleView={this.onToggleView}
          />
          : null
        }
      </div>
    )
  }
}

export default TutorResultsPartial
